"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const common_1 = require("../common");
/**
 * A vertex in a Crease object.
 */
class CreaseVertex {
    /**
     * Constructs a CreaseVertex object.
     * @param {number} index The index of this vertex.
     * @param {Vector2} position The 2D position of this vertex.
     */
    constructor(index, position) {
        // Check inputs.
        common_1.check.isInteger('index', index);
        common_1.check.is2DPoint('position', position);
        this._index = index;
        this._position = position;
        this._incidentEdges = [];
        this._incidentFaces = [];
    }
    /**
     * Returns the index of this vertex.
     * @returns {number} The index of this vertex.
     */
    get index() {
        return this._index;
    }
    /**
     * Returns the 2D position of this vertex.
     * @returns {Vector2} The 2D position of this vertex.
     */
    get position() {
        return this._position;
    }
    /**
     * Returns an array of edge references incident to this vertex.
     * @returns {CreaseEdgeRef[]} An array of edge references incident to this vertex.
     */
    get incidentEdges() {
        return this._incidentEdges;
    }
    /**
     * Returns an array of faces incident to this vertex.
     * @returns {CreaseFace[]} An array of faces incident to this vertex.
     */
    get incidentFaces() {
        return this._incidentFaces;
    }
    /**
     * Returns the ith incident edge reference after the specified incident edge or edge reference.
     * @param {CreaseEdgeRef} edgeRef An edge reference that is incident to this vertex.
     * @param {number} [i = 1] An integer offset. The default value of 1 gets the next edge.
     * @returns {CreaseEdgeRef} The ith next incident edge reference.
     */
    getNextEdgeRef(edgeRef, i = 1) {
        const { incidentEdges } = this;
        const index = incidentEdges.findIndex(r => r.edge === edgeRef.edge);
        if (index < 0) {
            throw new Error('The specified edge reference is not incident to this vertex.');
        }
        const n = incidentEdges.length;
        return incidentEdges[((index + i) % n + n) % n];
    }
    /**
     * Returns the ith incident edge reference before the specified incident edge or edge reference.
     * @param {CreaseEdgeRef} edgeRef An edge reference that is incident to this vertex.
     * @param {number} [i = 1] An integer offset. The default value of 1 gets the previous edge.
     * @returns {CreaseEdgeRef} The ith previous incident edge reference.
     */
    getPreviousEdgeRef(edgeRef, i = 1) {
        return this.getNextEdgeRef(edgeRef, -i);
    }
    /**
     * Ensures that this vertex's incident edges are sorted in counterclockwise order.
     */
    sortIncidentEdges() {
        // Calculate the angle of each edge.
        this._incidentEdges.forEach((edgeRef) => {
            // We'll use the angle made by the edge endpoints (ignoring edge curvature).
            const endDir = common_1.vec.subtract(edgeRef.vertex2.position, edgeRef.vertex1.position);
            edgeRef.endAngle = common_1.vec.getAngle(endDir);
            // We may also need to use the angle made by the edge's start tangent (accounting for
            // curvature).
            const edgePoints = edgeRef.allPoints;
            const startDir = common_1.geom.evaluateBezierStartTangent(edgePoints);
            edgeRef.startAngle = common_1.vec.getAngle(startDir);
            // Make sure the start tangent's angle is within pi of the endpoint angle (otherwise
            // sort order won't be well defined).
            if (edgeRef.startAngle - edgeRef.endAngle > Math.PI) {
                edgeRef.startAngle -= 2 * Math.PI;
            }
            else if (edgeRef.endAngle - edgeRef.startAngle > Math.PI) {
                edgeRef.startAngle += 2 * Math.PI;
            }
        });
        // Sort the edges by increasing angle.
        const epsilon = 0.001; // 0.001 radians = 0.06 degrees
        this._incidentEdges.sort((a, b) => {
            // Compare the angles to the edge endpoints, unless they're very close.
            const endDelta = a.endAngle - b.endAngle;
            if (Math.abs(endDelta) > epsilon) {
                return endDelta;
            }
            // Disambiguate using angles of the starting tangents.
            const startDelta = a.startAngle - b.startAngle;
            if (Math.abs(startDelta) > epsilon) {
                return startDelta;
            }
            // If the angles are identical, use the direction of boundary edges to disambiguate.
            return a.edge.vertex1 === this ? 1 : -1;
        });
        // Remove the angles from the edges.
        this._incidentEdges.forEach((edgeRef) => {
            delete edgeRef.startAngle;
            delete edgeRef.endAngle;
        });
    }
    /**
     * Assigns a new index to this vertex. Used internally.
     * @param index The new index for this vertex.
     */
    _setIndex(index) {
        this._index = index;
    }
    /**
     * Assigns a new position to this vertex. Used internally.
     * @param position The new position for this vertex.
     */
    _setPosition(position) {
        this._position = position;
    }
    /**
     * Replaces the incident edges for this vertex. Used internally.
     * @param incidentEdges The new incident edges.
     */
    _setIncidentEdges(incidentEdges) {
        this._incidentEdges = incidentEdges;
    }
    /**
     * Returns an object that can be serialized using `JSON.stringify`.
     * @returns {CreaseJSONVertex} The serializable representation of this vertex.
     */
    toJSON() {
        return { position: this.position };
    }
    /**
     * Checks deep equality between this and another CreaseVertex object
     * @param {CreaseVertex} vertex A vertex.
     * @param {boolean} [logDebug = false] A flag to log unequal elements, default to false.
     * @param {number} [epsilon = 1e-2] A small epsilon to use to compare floating point values.
     * @returns {boolean} Returns true if equal, else false.
     */
    equals(vertex, logDebug = false, epsilon = 1e-2) {
        let equal = true;
        const { position, index } = this;
        vertex.position.forEach((el, i) => {
            if (Math.abs(position[i] - el) > epsilon) {
                if (logDebug) {
                    /* eslint-disable-next-line no-console */
                    console.log(`Vertices at index ${index} have unequal position: ${JSON.stringify(position)}, ${JSON.stringify(vertex.position)}`);
                }
                equal = false;
            }
        });
        // We could add more here if needed.
        return equal;
    }
}
exports.CreaseVertex = CreaseVertex;
//# sourceMappingURL=CreaseVertex.js.map